import { Component, OnDestroy, OnInit, inject } from "@angular/core";
import { FormGroup, AbstractControl } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatTableDataSource } from "@angular/material/table";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { StringHelper } from "@tylertech/shared-module";
import { Command, LabelCommand } from "@tylertech/commands-module";
import { LgdFormControlFactory } from "@tylertech/lgd-reactive-forms";
import { ClassMetadata, IGetTransactionCriteria, ITransaction } from "@tylertech/tyler-api-types";
import { TransactionService } from "../../services";
type TransactionColumn = keyof ITransaction | "Total";
type FilterState<T extends string = string> = Partial<Record<T, string>>;
export interface TransactionDialogData
{
    readonly itemId: string;
}
// TODO - this component needs to be converted to use a metadata table completely instead of this reinvented wheel...
@Component({
    selector: "transaction-dialog-component",
    templateUrl: "./transactionsDialog.tmpl.html",
    styleUrls: ["./transactionsDialog.component.scss"],
    standalone: false
})
export class TransactionDialogComponent implements OnInit, OnDestroy
{
    private readonly dialogRef = inject<MatDialogRef<TransactionDialogComponent>>(MatDialogRef);
    private readonly data = inject<TransactionDialogData>(MAT_DIALOG_DATA);
    private $transactionService = inject(TransactionService);

    private readonly destructor$ = new Subject<void>();
    private readonly filterState: FilterState<TransactionColumn> = {};
    private metadata: ClassMetadata;
    readonly formGroup: FormGroup;
    readonly commands: Array<Command>;
    readonly defaultColumnNames: ReadonlyArray<TransactionColumn> = [
        "TransactionDate",
        "PostDate",
        "Type",
        "Number",
        "Description",
        "ProjectAccount",
        "Quantity",
        "Cost",
        "Total",
    ];

    tableData: MatTableDataSource<ITransaction>;
    isLoaded: boolean;
    constructor()
    {
        this.commands = [
            new LabelCommand({
                label: "Close",
                action: () => this.close(),
                getEnabled: () => true,
            }),
        ];
        this.formGroup = new FormGroup({
            totalFilter: LgdFormControlFactory.text(),
        });
        this.tableData = new MatTableDataSource<ITransaction>();
        this.tableData.filterPredicate = (data: ITransaction, filter: string): boolean =>
        {
            const state = JSON.parse(filter) as FilterState<TransactionColumn>;
            return Object.entries(state).reduce((result: boolean, [column, filterValue]: [string, string]): boolean =>
            {
                if (!result || !filterValue) return result;
                const value: string | undefined = data[column]?.toString().toLowerCase();
                if (!value || value.indexOf(filterValue) === -1)
                {
                    return false;
                }
                return true;
            }, true);
        };
    }

    private get itemId(): string
    {
        return this.data.itemId;
    }

    get columnNames(): ReadonlyArray<TransactionColumn>
    {
        return this.metadata
            ? this.defaultColumnNames.filter(
                fieldName => !this.metadata.Properties.find(x => x.Name === fieldName)?.Hidden || true
            )
            : this.defaultColumnNames;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;
        this.dialogRef.updateSize("60%", "80%");
        Promise.all([
            this.$transactionService.GetTransactionMetadata().then((classMetadata: ClassMetadata) =>
            {
                this.metadata = classMetadata;
                classMetadata.Properties.forEach(propertyMetadata =>
                {
                    const filterControl = LgdFormControlFactory.text();
                    this.formGroup.addControl(
                        StringHelper.toCamelCase(`${propertyMetadata.Name}Filter`),
                        filterControl
                    );
                    subscribeToValueChanges(filterControl, (newValue: string) =>
                    {
                        /* TODO(ET) - Filter table data... */
                        this.filterState[propertyMetadata.Name] = newValue.toLowerCase();
                        this.tableData.filter = JSON.stringify(this.filterState);
                    });
                });
            }),
            this.getTransactions().then((transactions: Array<ITransaction>) =>
            {
                this.tableData.data = transactions;
            }),
        ]).finally(() =>
        {
            this.isLoaded = true;
        });
        function subscribeToValueChanges<T>(control: AbstractControl, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges.pipe(takeUntil(self.destructor$)).subscribe(x =>
            {
                subscriptionFn(x);
            });
        }
    }

    private getTransactions(): Promise<Array<ITransaction>>
    {
        const criteria: IGetTransactionCriteria = {
            ItemId: this.itemId,
            FromDate: undefined,
            LocationCode: undefined,
            ProjectAccount: undefined,
            StockLocationCode: undefined,
            ToDate: undefined,
            Type: undefined,
        };
        return Promise.resolve(this.$transactionService.GetTransactions(criteria)).then(
            (result: Array<ITransaction>) =>
            {
                return [
                    ...result
                        .reduce((map: Map<string, ITransaction>, x: ITransaction): Map<string, ITransaction> =>
                        {
                            const transaction = map.get(x.Number);
                            if (!transaction)
                            {
                                map.set(x.Number, x);
                            }
                            else
                            {
                                transaction.Quantity += x.Quantity;
                            }
                            return map;
                        }, new Map<string, ITransaction>())
                        .values(),
                ];
            }
        );
    }

    close(): void
    {
        this.dialogRef.close();
    }
}
